// src/components/StationResultsModal.jsx
import React from 'react';
import '../styles/modal-animations.css'; // Pfad anpassen
import { teamKey, resultKey } from "../utils/teamKey";

// `startFilter`: Kategorie, mit der die Liste beim Oeffnen beginnt (aus der
// Ergebnisseite: die Kategorie, die dort gerade gewaehlt ist bzw. die des
// angeklickten Teams). `markiertesTeam`: Bezugsschluessel eines Teams, das in
// der Liste hervorgehoben wird – so findet man sich nach dem Klick aus der
// Team-Ansicht sofort wieder.
export default function StationResultsModal({
  open,
  onClose,
  station,
  teams,
  startFilter = "Alle",
  markiertesTeam = null,
}) {
  const allTeams = React.useMemo(() => teams || [], [teams]);
  const [filter, setFilter] = React.useState(startFilter);

  // Das Modal bleibt zwischen zwei Oeffnungen eingehaengt (es rendert nur
  // `null`), der Filter wuerde also von der letzten Station haengen bleiben.
  React.useEffect(() => {
    if (open) setFilter(startFilter);
  }, [open, startFilter, station]);

  const stationType = station?.station_type || "normal";

  // Hook für Escape-Taste und Body-Scroll
  React.useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);

  // Zuordnung ueber den Bezugsschluessel des Ergebnisses (team_id), nicht ueber
  // den angezeigten Namen.
  const getTeamEventType = React.useCallback((result) => {
    const key = resultKey(result);
    const team = allTeams.find(t => teamKey(t) === key);
    return team ? team.event_type : "Unbekannt";
  }, [allTeams]);

  // Anzeigename: bevorzugt der denormalisierte Name am Ergebnis, sonst der
  // aktuelle Name aus der Team-Liste.
  const getTeamName = React.useCallback((result) => {
    const key = resultKey(result);
    const team = allTeams.find(t => teamKey(t) === key);
    return team?.team_name ?? result?.team_name ?? result?.team ?? "Unbekannt";
  }, [allTeams]);

  const resultsArray = React.useMemo(
    () => (Array.isArray(station?.results) ? station.results : []),
    [station]
  );

  const adultsSorted = React.useMemo(() => {
    return resultsArray
      .filter(r => getTeamEventType(r) === "Erwachsene")
      .sort((a, b) => b.points - a.points);
  }, [resultsArray, getTeamEventType]);

  const kidsSorted = React.useMemo(() => {
    return resultsArray
      .filter(r => getTeamEventType(r) === "Kinder")
      .sort((a, b) => b.points - a.points);
  }, [resultsArray, getTeamEventType]);

  const filteredResults = React.useMemo(() => {
    if (filter === "Erwachsene") return adultsSorted;
    if (filter === "Kinder") return kidsSorted;
    return [];
  }, [filter, adultsSorted, kidsSorted]);

  if (!open || !station) {
    return null;
  }

  // Einzelner Listeneintrag. Platz = der in der Berechnung vergebene
  // Stationsplatz (`rank`); bei Gleichstand teilen sich Teams den Platz (1-1-3).
  // Frueher stand hier die Listenposition – zwei punktgleiche Teams wurden als
  // #1 und #2 angezeigt. Rueckfall auf die Position nur fuer Altbestand ohne rank.
  const ResultRow = ({ rank, result }) => (
    <div
      className={`flex items-center gap-3 p-3 bg-secondary-800 rounded-lg shadow-inner ${
        markiertesTeam != null && resultKey(result) === markiertesTeam
          ? "ring-2 ring-primary-500"
          : ""
      }`}
    >
      {/* Kompakte Platzierung */}
      <div className="flex items-center justify-center w-8 h-6 rounded bg-yellow-400 text-black font-bold text-xs flex-shrink-0">
        #{rank}
      </div>

      {/* Team Info */}
      <div className="flex-1 min-w-0">
        <h3 className="text-base font-semibold text-gray-100 truncate leading-tight">{getTeamName(result)}</h3>
        <p className="text-xs text-gray-400 truncate">{getTeamEventType(result)}</p>
      </div>

      {/* Ergebnisse */}
      <div className="flex flex-col items-end text-right flex-shrink-0 min-w-0">
        <div className="flex items-center gap-1">
          <span className="text-lg font-bold text-yellow-400">{result.points || 0}</span>
          <span className="text-xs text-gray-400">Pkt</span>
        </div>
        {stationType === "estimate" ? (
        <p className="text-xs text-gray-400 whitespace-nowrap">
            Schätzwert: {result.estimate_value ?? "-"} {station.unit || ""}
        </p>
        ) : (
        <p className="text-xs text-gray-400 whitespace-nowrap">
            {result.performance_score != null
            ? `${result.performance_score}${station.unit ? " " + station.unit : ""}`
            : "Kein Ergebnis"}
        </p>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overlay-fade-in">
      <div className="relative w-full max-w-4xl bg-secondary-900 rounded-xl shadow-2xl p-6 transform-gpu scale-95 modal-scale-up">
        {/* Titel und Schließen-Button */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-100">{station.station_name}</h2>
            <p className="text-sm font-medium text-gray-400">
              Station {station.station_number}
              {station.unit && <span className="ml-2">• Einheit: {station.unit}</span>}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-gray-400 hover:text-gray-100 hover:bg-secondary-800 transition-colors"
            aria-label="Modal schließen"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Filter-Buttons */}
        <div className="flex gap-2 mb-4">
          {["Alle", "Erwachsene", "Kinder"].map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === type
                  ? "bg-yellow-500 text-black"
                  : "bg-secondary-800 text-gray-300 hover:bg-secondary-700"
              }`}
            >
              {type}
            </button>
          ))}
        </div>

        {/* Ergebnisliste */}
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {filter === "Alle" ? (
            <>
              {/* Erwachsene */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-md font-semibold text-gray-200">Erwachsene</h3>
                  <span className="text-sm text-gray-400">{adultsSorted.length} Einträge</span>
                </div>
                {adultsSorted.length > 0 ? (
                  <div className="space-y-2">
                    {adultsSorted.map((res, i) => (
                      <ResultRow key={`adult-${resultKey(res)}-${i}`} rank={res.rank ?? i + 1} result={res} />
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-400 p-3 rounded-lg bg-secondary-800">Keine Ergebnisse für Erwachsene.</div>
                )}
              </div>

              <div className="my-3 border-t border-secondary-800" />

              {/* Kinder */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-md font-semibold text-gray-200">Kinder</h3>
                  <span className="text-sm text-gray-400">{kidsSorted.length} Einträge</span>
                </div>
                {kidsSorted.length > 0 ? (
                  <div className="space-y-2">
                    {kidsSorted.map((res, i) => (
                      <ResultRow key={`kid-${resultKey(res)}-${i}`} rank={res.rank ?? i + 1} result={res} />
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-400 p-3 rounded-lg bg-secondary-800">Keine Ergebnisse für Kinder.</div>
                )}
              </div>
            </>
          ) : (
            <>
              {filteredResults.length > 0 ? (
                filteredResults.map((res, i) => (
                  <ResultRow key={`${filter}-${resultKey(res)}-${i}`} rank={res.rank ?? i + 1} result={res} />
                ))
              ) : (
                <div className="text-center p-8 text-gray-400">
                  Noch keine Ergebnisse für diesen Filter verfügbar.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
